<script lang="ts">
import type { FileInfoInput } from '#layers/assets/composables/use-file-info';
import type { GlobalFileId } from '#layers/data-model/public';

export interface RenderAssetImageFileInput extends FileInfoInput {
	id: GlobalFileId;
}

export interface RenderAssetImageProps {
	/**
	 * The file to render. Can be a file object or just a file ID string.
	 */
	file: RenderAssetImageFileInput | GlobalFileId;
	/**
	 * Indicate that this is a workspace image and should be prefixed with the workspace slug.
	 * Set to false for system-level assets.
	 */
	workspace?: boolean;
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { AssetUrlScopes, useAssetUrl } from '#layers/assets/composables/use-asset-url';
import { useFileInfo } from '#layers/assets/composables/use-file-info';
import { DynamicLogo } from '#layers/ui/public';

const props = withDefaults(
	defineProps<RenderAssetImageProps>(),
	{ workspace: true },
);

const fileId = computed(() => typeof props.file === 'string' ? props.file : props.file.id);
const fileObject = computed(() => typeof props.file === 'string' ? null : props.file);
const src = useAssetUrl(fileId, {
	scope: () => props.workspace ? AssetUrlScopes.Workspace : AssetUrlScopes.System,
});

const { title } = useFileInfo(fileObject);
const hasError = ref(false);

watch(() => props.file, () => {
	hasError.value = false;
});
</script>

<template>
	<img
		v-if="!hasError && src"
		:src
		:alt="title"
		v-bind="$attrs"
		@load="hasError = false"
		@error="hasError = true"
	>
	<slot v-else name="error">
		<DynamicLogo
			v-bind="$attrs"
		/>
	</slot>
</template>
